/**
 * AP-34 Nachtrag 3 (PT-9, PT-10) — **der Abgleich als Arbeitsliste, rein**: in welchem Stand eine
 * Rechnungsposition ist, welche Bestellpositionen zu ihr passen könnten und warum.
 *
 * Die Kandidaten rechnet die Maske selbst, mit derselben Funktion wie der Server (`zuordnungsWert`
 * aus `@symphenia/shared-types`). Vorher kamen sie nur in der Antwort von „Zuordnung vorschlagen" —
 * sie lebten im Zustand der Seite, waren nach dem Neuladen weg, und eine Zeile ohne Kandidat ließ
 * sich gar nicht zuordnen.
 */
import { einheitGleich, nettoJeEinheit, textAehnlichkeit, vergleichspreis, zuordnungsWert } from '@symphenia/shared-types'
import type { IncomingInvoiceLine, OpenPosition } from '../types/incoming-invoices.types'

/** `OFFEN` zu klären · `PRUEFEN` vorgeschlagen, zu bestätigen · `ERLEDIGT` */
export type AbgleichStand = 'OFFEN' | 'PRUEFEN' | 'ERLEDIGT'

type Zeile = Pick<IncomingInvoiceLine, 'lineType' | 'sourceLineId' | 'pairingConfirmed' | 'withoutOrder'>

/** Positionen mit Wert, die einen Bezug haben können: Artikel und Nebenkosten. Abzüge und Text gehören nicht in den Abgleich. */
export const imAbgleich = (z: Pick<IncomingInvoiceLine, 'lineType'>): boolean => z.lineType === 'ITEM' || z.lineType === 'CHARGE'

/**
 * Nebenkosten brauchen keinen Bezug (9.3, Freigrenze der Einstellungen); eine Artikelzeile ist ohne Bezug
 * erst geklärt, wenn jemand sie ausdrücklich als „ohne Bezug" gekennzeichnet hat (E-8).
 */
export function abgleichStand(z: Zeile): AbgleichStand {
  if (z.sourceLineId) return z.pairingConfirmed ? 'ERLEDIGT' : 'PRUEFEN'
  return z.lineType === 'CHARGE' || z.withoutOrder ? 'ERLEDIGT' : 'OFFEN'
}

/** Abweichungen, die jemand angesehen haben muss — „Preis niedriger" ist nur ein Hinweis. */
const ZU_PRUEFEN = ['QUANTITY_EXCEEDS_OPEN', 'PRICE_ABOVE_TOLERANCE', 'UNIT_MISMATCH', 'SOURCE_EXHAUSTED', 'SOURCE_GONE']
/** Darf ein Vorschlag in einem Zug mit den anderen bestätigt werden? Nur ohne Abweichung — jede Abweichung einzeln. */
export const ohneAbweichung = (z: Pick<IncomingInvoiceLine, 'deviations'>): boolean => !z.deviations.some((d) => ZU_PRUEFEN.includes(d))

export interface Kandidat {
  position: OpenPosition
  wert: number
  /** worin Zeile und Position übereinstimmen — die Begründung am Knopf */
  passt: ('Bezeichnung' | 'Menge' | 'Einheit' | 'Preis')[]
}

type ZeileZumVergleich = Pick<IncomingInvoiceLine, 'id' | 'lineType' | 'name' | 'articleId' | 'articleNumber' | 'supplierArticleNumber' | 'quantity' | 'unitCode' | 'unitPrice' | 'priceUnit' | 'discountPercent'>

const EPS = 0.0005
/** Hier geht es ums Erkennen, nicht ums Prüfen — dieselbe Untergrenze wie in `zuordnungsWert`. */
const ERKENNEN = { preisProzent: 5, preisBetrag: 0 }

export function worinPasst(z: ZeileZumVergleich, p: OpenPosition): Kandidat['passt'] {
  const soll = vergleichspreis(p)
  const sollNetto = nettoJeEinheit(soll.einzelpreis, soll.preiseinheit, soll.rabattProzent)
  const istNetto = nettoJeEinheit(z.unitPrice, z.priceUnit, z.discountPercent)
  const passt: Kandidat['passt'] = []
  if (textAehnlichkeit(z.name, p.bezeichnung) > 0) passt.push('Bezeichnung')
  if (z.quantity > 0 && z.quantity - p.offen <= EPS) passt.push('Menge')
  if (z.unitCode && einheitGleich(z.unitCode, p.unitCode)) passt.push('Einheit')
  if (sollNetto > 0 && Math.abs(istNetto - sollNetto) / sollNetto <= ERKENNEN.preisProzent / 100 + 1e-9) passt.push('Preis')
  return passt
}

/**
 * Ab diesem Wert ist eine Position ein Kandidat: Menge **und** Preis, oder die Bezeichnung mit einem von beiden.
 * Darunter passt nur die Menge oder nur die Einheit — das trifft bei einer Frachtzeile auf jede Position zu,
 * in die „1" hineinpasst, und stünde dann als „am nächsten" da.
 */
export const KANDIDAT_AB = 0.25

/** Die besten offenen Positionen zu einer Zeile, absteigend — nur, was in mehr als einer Nebensache übereinstimmt. */
export function kandidaten(z: ZeileZumVergleich, positionen: readonly OpenPosition[], hoechstens = 3): Kandidat[] {
  return positionen
    .filter((p) => p.offen > EPS)
    .map((p) => ({ position: p, wert: zuordnungsWert(z, p, ERKENNEN), passt: worinPasst(z, p) }))
    .filter((k) => k.wert >= KANDIDAT_AB)
    .sort((a, b) => b.wert - a.wert)
    .slice(0, hoechstens)
}

/** Freie Suche über alle offenen Positionen des Lieferanten: jedes Wort muss irgendwo vorkommen. */
export function suchePositionen(positionen: readonly OpenPosition[], text: string): OpenPosition[] {
  const woerter = text.toLowerCase().split(/\s+/).filter(Boolean)
  if (!woerter.length) return []
  return positionen.filter((p) => {
    if (p.offen <= EPS) return false
    const heu = [p.bezeichnung, p.bestellnummer, p.bestellzeileNr, p.articleNumber, p.supplierArticleNumber, p.projectNumber, p.projectName].filter(Boolean).join(' ').toLowerCase()
    return woerter.every((w) => heu.includes(w))
  })
}

export interface BestellGruppe {
  bestellungId: string
  nummer: string
  datum: string | null
  projekt: string | null
  /** Positionen dieser Bestellung, die in dieser Rechnung stehen */
  inRechnung: OpenPosition[]
  /** Gegenprobe: was von der Bestellung nach dieser Rechnung offen bleibt */
  bleibtOffen: OpenPosition[]
}

/** Die Bestellungen, aus denen diese Rechnung etwas verrechnet — mit dem, was von ihnen offen bleibt. */
export function bestellGruppen(positionen: readonly OpenPosition[]): BestellGruppe[] {
  const aus = new Map<string, BestellGruppe>()
  for (const p of positionen) {
    const g = aus.get(p.bestellungId) ?? { bestellungId: p.bestellungId, nummer: p.bestellnummer, datum: p.bestelldatum, projekt: [p.projectNumber, p.projectName].filter(Boolean).join(' ') || null, inRechnung: [], bleibtOffen: [] }
    if (p.inDieserRechnung !== 0) g.inRechnung.push(p)
    if (p.offen > EPS) g.bleibtOffen.push(p)
    aus.set(p.bestellungId, g)
  }
  return [...aus.values()].filter((g) => g.inRechnung.length > 0)
}
